import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Header from '../Common/Header';
import { api } from '../../services/api';
import './VoterSurvey.css';

const VoterSurvey = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // ==================== STATE ====================
  const [formData, setFormData] = useState({
    voterId: '',
    fullName: '',
    email: '',
    phoneNumber: '',
    age: '',
    gender: '',
    district: '',
    constituency: '',
    selectedCandidate: '',
    mainConcern: '',
    topPriority: '',
    governmentPerformance: '',
    mostImportantIssue: '',
    preferredChiefMinister: '',
    preferredChiefMinisterOther: '',
    expectedWinner: ''
  });

  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [constituencies, setConstituencies] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(true);
  const [loadingConstituencies, setLoadingConstituencies] = useState(false);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [errors, setErrors] = useState({});

  // ==================== CHIEF MINISTER OPTIONS ====================
  const chiefMinisterOptions = [
    { id: 'nitish-kumar', name: 'Nitish Kumar' },
    { id: 'tejashwi-yadav', name: 'Tejashwi Prasad Yadav' },
    { id: 'tej-pratap-yadav', name: 'Tej Pratap Yadav' },
    { id: 'prashant-kishor', name: 'Prashant Kishor' },
    { id: 'chirag-paswan', name: 'Chirag Paswan' },
    { id: 'samrat-choudhary', name: 'Samrat Choudhary' },
    { id: 'mukesh-sahani', name: 'Mukesh Sahani' },
    { id: 'jitan-ram-manjhi', name: 'Jitan Ram Manjhi' },
    { id: 'shahnawaz-hussain', name: 'Shahnawaz Hussain' },
    { id: 'nityanand-rai', name: 'Nityanand Rai' },
    { id: 'other', name: t('voterSurvey.other') || 'Other' }
  ];

  // ==================== TRANSLATED OPTIONS ====================
  const mainConcerns = [
    { id: 'development', label: t('voterSurvey.concerns.development') || 'Development' },
    { id: 'employment', label: t('voterSurvey.concerns.employment') || 'Employment' },
    { id: 'education', label: t('voterSurvey.concerns.education') || 'Education' },
    { id: 'healthcare', label: t('voterSurvey.concerns.healthcare') || 'Healthcare' },
    { id: 'agriculture', label: t('voterSurvey.concerns.agriculture') || 'Agriculture' },
    { id: 'law-order', label: t('voterSurvey.concerns.lawOrder') || 'Law & Order' },
    { id: 'corruption', label: t('voterSurvey.concerns.corruption') || 'Corruption' },
    { id: 'electricity', label: t('voterSurvey.concerns.electricity') || 'Electricity' }
  ];

  const topPriorities = [
    { id: 'roads', label: t('voterSurvey.priorities.roads') || 'Roads & Transport' },
    { id: 'hospitals', label: t('voterSurvey.priorities.hospitals') || 'Hospitals' },
    { id: 'schools', label: t('voterSurvey.priorities.schools') || 'Schools' },
    { id: 'jobs', label: t('voterSurvey.priorities.jobs') || 'Jobs' },
    { id: 'water', label: t('voterSurvey.priorities.water') || 'Water Supply' },
    { id: 'safety', label: t('voterSurvey.priorities.safety') || 'Safety' },
    { id: 'prices', label: t('voterSurvey.priorities.prices') || 'Food Prices' },
    { id: 'internet', label: t('voterSurvey.priorities.internet') || 'Internet' }
  ];

  const governmentPerformance = [
    { id: 'excellent', label: t('voterSurvey.performance.excellent') || 'Excellent', value: 5 },
    { id: 'good', label: t('voterSurvey.performance.good') || 'Good', value: 4 },
    { id: 'average', label: t('voterSurvey.performance.average') || 'Average', value: 3 },
    { id: 'poor', label: t('voterSurvey.performance.poor') || 'Poor', value: 2 },
    { id: 'very-poor', label: t('voterSurvey.performance.veryPoor') || 'Very Poor', value: 1 }
  ];

  const mostImportantIssues = [
    { id: 'youth-unemployment', label: t('voterSurvey.issues.youthUnemployment') || 'Youth Unemployment' },
    { id: 'farmer-distress', label: t('voterSurvey.issues.farmerDistress') || 'Farmer Distress' },
    { id: 'poor-infrastructure', label: t('voterSurvey.issues.poorInfrastructure') || 'Poor Infrastructure' },
    { id: 'education-quality', label: t('voterSurvey.issues.educationQuality') || 'Education Quality' },
    { id: 'healthcare-access', label: t('voterSurvey.issues.healthcareAccess') || 'Healthcare Access' },
    { id: 'women-safety', label: t('voterSurvey.issues.womenSafety') || 'Women Safety' },
    { id: 'corruption', label: t('voterSurvey.issues.corruption') || 'Corruption' },
    { id: 'electricity', label: t('voterSurvey.issues.electricity') || 'Electricity' }
  ];

  // ==================== LOAD DISTRICTS ====================
  useEffect(() => {
    fetchDistricts();
  }, []);

  const fetchDistricts = async () => {
    try {
      setLoadingDistricts(true);
      console.log('📍 Fetching districts...');
      const response = await api.getDistricts();
      console.log('✅ Districts fetched:', response);
      setDistricts(response || []);
      setLoadingDistricts(false);
    } catch (error) {
      console.error('❌ Error fetching districts:', error);
      // ❌ REMOVED: alert(t('common.loadError') || 'Failed to load districts');
      toast.error(t('common.loadError') || 'Failed to load districts');
      setLoadingDistricts(false);
    }
  };

  // ==================== LOAD CONSTITUENCIES ====================
  useEffect(() => {
    if (formData.district) {
      fetchConstituencies(formData.district);
      setFormData(prev => ({
        ...prev,
        constituency: '',
        selectedCandidate: '',
        expectedWinner: ''
      }));
      setConstituencies([]);
      setCandidates([]);
    } else {
      setConstituencies([]);
      setCandidates([]);
    }
  }, [formData.district]);

  const fetchConstituencies = async (district) => {
    try {
      setLoadingConstituencies(true);
      console.log('📍 Fetching constituencies for district:', district);
      const response = await api.getConstituenciesByDistrict(district);
      console.log('✅ Constituencies fetched:', response);
      setConstituencies(response || []);
      setLoadingConstituencies(false);
    } catch (error) {
      console.error('❌ Error fetching constituencies:', error);
      // ❌ REMOVED: alert(t('common.loadError') || 'Failed to load constituencies');
      toast.error(t('common.loadError') || 'Failed to load constituencies');
      setLoadingConstituencies(false);
    }
  };

  // ==================== LOAD CANDIDATES ====================
  useEffect(() => {
    if (formData.constituency) {
      fetchCandidates(formData.constituency);
      setFormData(prev => ({
        ...prev,
        selectedCandidate: '',
        expectedWinner: ''
      }));
      setCandidates([]);
    } else {
      setCandidates([]);
    }
  }, [formData.constituency]);

  const fetchCandidates = async (constituencyName) => {
    try {
      setLoadingCandidates(true);
      console.log('👥 Fetching candidates for constituency:', constituencyName);
      const response = await api.getCandidatesByConstituencyName(constituencyName);
      console.log('✅ Candidates fetched:', response);
      if (response && response.success && response.data) {
        setCandidates(response.data);
      } else {
        setCandidates([]);
      }
      setLoadingCandidates(false);
    } catch (error) {
      console.error('❌ Error fetching candidates:', error);
      // ❌ REMOVED: alert(t('common.loadError') || 'Failed to load candidates');
      toast.error(t('common.loadError') || 'Failed to load candidates');
      setLoadingCandidates(false);
    }
  };

  // ==================== CHECK EXISTING SURVEY ====================
  const checkExistingSurvey = async (phoneNumber) => {
    if (phoneNumber.length !== 10) return;

    try {
      setChecking(true);
      const response = await api.checkVoterSurvey(phoneNumber);
      if (response && response.exists) {
        // ❌ REMOVED: alert(t('voterSurvey.alreadySubmitted') || 'You have already submitted a survey!');
        console.log('⚠️ User already submitted survey');
        toast.warning(t('voterSurvey.alreadySubmitted') || 'You have already submitted a survey!');
        setTimeout(() => {
          navigate('/survey/results');
        }, 2000);
      }
      setChecking(false);
    } catch (error) {
      console.error('❌ Error checking survey:', error);
      setChecking(false);
    }
  };

  // ==================== HANDLE INPUT CHANGE ====================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Reset "Other" input if CM selection changes
    if (name === 'preferredChiefMinister' && value !== 'Other') {
      setFormData(prev => ({
        ...prev,
        preferredChiefMinisterOther: ''
      }));
    }

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    if (name === 'phoneNumber' && value.length === 10) {
      checkExistingSurvey(value);
    }
  };

  // ==================== VALIDATE FORM ====================
  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName || formData.fullName.trim().length < 3) {
      newErrors.fullName = t('voterSurvey.errors.fullName') || 'Full name must be at least 3 characters';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('voterSurvey.errors.email') || 'Valid email is required';
    }

    if (!formData.phoneNumber || !/^\d{10}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = t('voterSurvey.errors.phoneNumber') || 'Phone number must be 10 digits';
    }

    if (!formData.age || formData.age < 18 || formData.age > 120) {
      newErrors.age = t('voterSurvey.errors.age') || 'Age must be between 18 and 120';
    }

    if (!formData.gender) {
      newErrors.gender = t('voterSurvey.errors.gender') || 'Gender is required';
    }

    if (!formData.district) {
      newErrors.district = t('voterSurvey.errors.district') || 'District is required';
    }

    if (!formData.constituency) {
      newErrors.constituency = t('voterSurvey.errors.constituency') || 'Constituency is required';
    }

    if (!formData.selectedCandidate) {
      newErrors.selectedCandidate = t('voterSurvey.errors.candidate') || 'Please select a candidate';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ==================== HANDLE SUBMIT ====================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // ❌ REMOVED: alert(t('voterSurvey.fillAllFields') || 'Please fill all required fields correctly');
      console.log('❌ Form validation failed');
      toast.error(t('voterSurvey.fillAllFields') || 'Please fill all required fields correctly');
      return;
    }

    try {
      setLoading(true);

      // Use custom CM name if "Other" is selected
      const finalCMPreference = formData.preferredChiefMinister === 'Other' 
        ? formData.preferredChiefMinisterOther.trim() 
        : formData.preferredChiefMinister;

      const surveyData = {
        voterId: formData.voterId?.trim() || null,
        fullName: formData.fullName.trim(),
        email: formData.email?.trim() || null,
        phoneNumber: formData.phoneNumber.trim(),
        age: parseInt(formData.age),
        gender: formData.gender,
        district: formData.district,
        constituency: formData.constituency,
        selectedCandidate: formData.selectedCandidate,
        mainConcern: formData.mainConcern?.trim() || null,
        topPriority: formData.topPriority?.trim() || null,
        governmentPerformance: formData.governmentPerformance?.trim() || null,
        mostImportantIssue: formData.mostImportantIssue?.trim() || null,
        preferredChiefMinister: finalCMPreference || null,
        expectedWinner: formData.expectedWinner?.trim() || null
      };

      console.log('📤 Submitting survey:', surveyData);

      const response = await api.submitVoterSurvey(surveyData);

      if (response && response.success) {
        // ❌ REMOVED: alert(t('voterSurvey.submitSuccess') || 'Survey submitted successfully! 🎉');
        console.log('✅ Survey submitted successfully');
        toast.success(t('voterSurvey.submitSuccess') || 'Survey submitted successfully! 🎉');
        
        setTimeout(() => {
          navigate('/survey/thank-you', { 
            state: { surveyId: response.surveyId } 
          });
        }, 1500);
      } else {
        // ❌ REMOVED: alert(response?.message || t('voterSurvey.submitError') || 'Survey submission failed');
        console.error('❌ Survey submission failed:', response?.message);
        toast.error(response?.message || t('voterSurvey.submitError') || 'Survey submission failed');
      }

      setLoading(false);
    } catch (error) {
      console.error('❌ Survey submission error:', error);
      // ❌ REMOVED: alert(error.message || t('common.connectionError') || 'Failed to submit survey');
      toast.error(error.message || t('common.connectionError') || 'Failed to submit survey');
      setLoading(false);
    }
  };

  // ==================== RENDER ====================
  return (
    <>
      <Header />
      
      <div className="voter-survey-page">
        <div className="survey-container">
          <div className="survey-header">
            <h1>{t('voterSurvey.title') || 'Bihar Election Voter Survey 2025'}</h1>
            <p>{t('voterSurvey.subtitle') || 'Share your opinion and help shape Bihar\'s future'}</p>
          </div>

          <form onSubmit={handleSubmit} className="survey-form">
            {/* ==================== LOCATION INFORMATION ==================== */}
            <div className="form-section">
              <div className="form-row">
                <div className="form-group">
                  <label>{t('results.district') || 'District'} *</label>
                  <select
                    name="district"
                    value={formData.district}
                    onChange={handleChange}
                    required
                    disabled={loadingDistricts}
                    className={errors.district ? 'error' : ''}
                    aria-label={t('results.district') || 'District'}
                  >
                    <option value="">
                      {loadingDistricts 
                        ? t('common.loading') || 'Loading districts...' 
                        : t('voterSurvey.selectDistrict') || 'Select District'}
                    </option>
                    {districts.map((district, index) => (
                      <option key={index} value={district}>
                        {district}
                      </option>
                    ))}
                  </select>
                  {errors.district && <span className="error-message">{errors.district}</span>}
                </div>

                <div className="form-group">
                  <label>{t('voterSurvey.constituencyLabel') || 'Constituency (Assembly)'} *</label>
                  <select
                    name="constituency"
                    value={formData.constituency}
                    onChange={handleChange}
                    required
                    disabled={!formData.district || loadingConstituencies}
                    className={errors.constituency ? 'error' : ''}
                    aria-label={t('nominations.constituencies') || 'Constituency'}
                  >
                    <option value="">
                      {!formData.district 
                        ? t('voterSurvey.selectDistrictFirst') || 'Select district first' 
                        : loadingConstituencies 
                        ? t('common.loading') || 'Loading constituencies...' 
                        : t('voterSurvey.selectConstituency') || 'Select Constituency'}
                    </option>
                    {constituencies.map((constituency) => (
                      <option key={constituency.id} value={constituency.name}>
                        {constituency.code} - {constituency.name}
                      </option>
                    ))}
                  </select>
                  {errors.constituency && <span className="error-message">{errors.constituency}</span>}
                </div>
              </div>
            </div>

            {/* ==================== CANDIDATE SELECTION ==================== */}
            <div className="form-section">
              <div className="form-group">
                <label>{t('voterSurvey.candidateQuestion') || 'Which candidate will you vote for?'} *</label>
                <select
                  name="selectedCandidate"
                  value={formData.selectedCandidate}
                  onChange={handleChange}
                  required
                  disabled={!formData.constituency || loadingCandidates}
                  className={errors.selectedCandidate ? 'error' : ''}
                  aria-label={t('voterSurvey.selectCandidate') || 'Select Candidate'}
                >
                  <option value="">
                    {!formData.constituency 
                      ? t('voterSurvey.selectConstituencyFirst') || 'Select constituency first' 
                      : loadingCandidates 
                      ? t('common.loading') || 'Loading candidates...' 
                      : t('voterSurvey.selectCandidate') || 'Select Candidate'}
                  </option>
                  {candidates.map((candidate) => (
                    <option key={candidate.candidateId} value={candidate.candidateName}>
                      {candidate.candidateName} ({candidate.partyName})
                    </option>
                  ))}
                </select>
                {errors.selectedCandidate && <span className="error-message">{errors.selectedCandidate}</span>}
              </div>
            </div>

            {/* ==================== SURVEY QUESTIONS (OPTIONAL) ==================== */}
            <div className="form-section">
              {/* Expected Winner */}
              <div className="form-group">
                <label>{t('voterSurvey.winnerQuestion') || 'In your opinion, who is winning from your constituency?'}</label>
                <select
                  name="expectedWinner"
                  value={formData.expectedWinner}
                  onChange={handleChange}
                  disabled={!formData.constituency || loadingCandidates}
                  aria-label={t('voterSurvey.expectedWinner') || 'Expected Winner'}
                >
                  <option value="">
                    {!formData.constituency 
                      ? t('voterSurvey.selectConstituencyFirst') || 'Select constituency first' 
                      : loadingCandidates 
                      ? t('common.loading') || 'Loading candidates...' 
                      : t('voterSurvey.selectCandidate') || 'Select Candidate'}
                  </option>
                  {candidates.map((candidate) => (
                    <option key={candidate.candidateId} value={candidate.candidateName}>
                      {candidate.candidateName} ({candidate.partyName})
                    </option>
                  ))}
                </select>
              </div>

              {/* Chief Minister Dropdown */}
              <div className="form-group">
                <label>{t('voterSurvey.cmQuestion') || 'Who would you like to see as Chief Minister?'}</label>
                <select
                  name="preferredChiefMinister"
                  value={formData.preferredChiefMinister}
                  onChange={handleChange}
                  aria-label={t('voterSurvey.preferredCM') || 'Preferred Chief Minister'}
                >
                  <option value="">{t('voterSurvey.selectCM') || 'Select Chief Minister'}</option>
                  {chiefMinisterOptions.map(cm => (
                    <option key={cm.id} value={cm.name}>
                      {cm.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Show text input if "Other" is selected */}
              {formData.preferredChiefMinister === 'Other' && (
                <div className="form-group">
                  <label>{t('voterSurvey.enterCMName') || 'Enter Chief Minister Name'}</label>
                  <input
                    type="text"
                    name="preferredChiefMinisterOther"
                    value={formData.preferredChiefMinisterOther}
                    onChange={handleChange}
                    placeholder={t('voterSurvey.enterName') || 'Enter name'}
                    aria-label={t('voterSurvey.otherCM') || 'Other CM Name'}
                  />
                </div>
              )}

              <div className="form-group">
                <label>{t('voterSurvey.mainConcernQuestion') || 'What is your main concern?'}</label>
                <select
                  name="mainConcern"
                  value={formData.mainConcern}
                  onChange={handleChange}
                  aria-label={t('voterSurvey.mainConcern') || 'Main Concern'}
                >
                  <option value="">{t('voterSurvey.selectConcern') || 'Select main concern'}</option>
                  {mainConcerns.map(concern => (
                    <option key={concern.id} value={concern.label}>
                      {concern.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>{t('voterSurvey.topPriorityQuestion') || 'What is your top priority for development?'}</label>
                <select
                  name="topPriority"
                  value={formData.topPriority}
                  onChange={handleChange}
                  aria-label={t('voterSurvey.topPriority') || 'Top Priority'}
                >
                  <option value="">{t('voterSurvey.selectPriority') || 'Select priority'}</option>
                  {topPriorities.map(priority => (
                    <option key={priority.id} value={priority.label}>
                      {priority.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>{t('voterSurvey.importantIssueQuestion') || 'What is the most important issue facing Bihar?'}</label>
                <select
                  name="mostImportantIssue"
                  value={formData.mostImportantIssue}
                  onChange={handleChange}
                  aria-label={t('voterSurvey.mostImportantIssue') || 'Most Important Issue'}
                >
                  <option value="">{t('voterSurvey.selectIssue') || 'Select issue'}</option>
                  {mostImportantIssues.map(issue => (
                    <option key={issue.id} value={issue.label}>
                      {issue.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>{t('voterSurvey.performanceQuestion') || 'How do you rate government performance?'}</label>
                <select
                  name="governmentPerformance"
                  value={formData.governmentPerformance}
                  onChange={handleChange}
                  aria-label={t('voterSurvey.governmentPerformance') || 'Government Performance'}
                >
                  <option value="">{t('voterSurvey.selectRating') || 'Select rating'}</option>
                  {governmentPerformance.map(perf => (
                    <option key={perf.id} value={perf.label}>
                      {perf.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* ==================== PERSONAL INFORMATION ==================== */}
            <div className="form-section">
              <div className="form-row">
                <div className="form-group">
                  <label>{t('voterCertificate.fullName') || 'Full Name'} *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder={t('voterCertificate.enterFullName') || 'Enter your full name'}
                    required
                    className={errors.fullName ? 'error' : ''}
                    aria-label={t('voterCertificate.fullName') || 'Full Name'}
                  />
                  {errors.fullName && <span className="error-message">{errors.fullName}</span>}
                </div>

                <div className="form-group">
                  <label>{t('voterSurvey.emailOptional') || 'Email (Optional)'}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('voterSurvey.enterEmail') || 'Enter your email'}
                    className={errors.email ? 'error' : ''}
                    aria-label={t('candidateDetails.email') || 'Email'}
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('voterCertificate.phone') || 'Phone Number'} *</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder={t('voterSurvey.tenDigit') || '10-digit mobile number'}
                    required
                    maxLength="10"
                    className={errors.phoneNumber ? 'error' : ''}
                    aria-label={t('voterCertificate.phone') || 'Phone Number'}
                  />
                  {checking && <span className="checking-message">{t('voterSurvey.checking') || 'Checking...'}</span>}
                  {errors.phoneNumber && <span className="error-message">{errors.phoneNumber}</span>}
                </div>

                <div className="form-group">
                  <label>{t('results.age') || 'Age'} *</label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleChange}
                    placeholder={t('voterSurvey.yourAge') || 'Your age'}
                    required
                    min="18"
                    max="120"
                    className={errors.age ? 'error' : ''}
                    aria-label={t('results.age') || 'Age'}
                  />
                  {errors.age && <span className="error-message">{errors.age}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('voterCertificate.gender') || 'Gender'} *</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                    className={errors.gender ? 'error' : ''}
                    aria-label={t('voterCertificate.gender') || 'Gender'}
                  >
                    <option value="">{t('voterCertificate.selectGender') || 'Select Gender'}</option>
                    <option value="Male">{t('voterCertificate.male') || 'Male'}</option>
                    <option value="Female">{t('voterCertificate.female') || 'Female'}</option>
                    <option value="Other">{t('voterCertificate.other') || 'Other'}</option>
                  </select>
                  {errors.gender && <span className="error-message">{errors.gender}</span>}
                </div>

                <div className="form-group">
                  <label>{t('voterSurvey.voterIdOptional') || 'Voter ID (Optional)'}</label>
                  <input
                    type="text"
                    name="voterId"
                    value={formData.voterId}
                    onChange={handleChange}
                    placeholder={t('voterSurvey.enterVoterId') || 'Enter your Voter ID'}
                    maxLength="10"
                    aria-label={t('voterCertificate.voterId') || 'Voter ID'}
                  />
                </div>
              </div>
            </div>

            {/* ==================== SUBMIT BUTTON ==================== */}
            <div className="form-actions">
              <button 
                type="submit" 
                className="submit-btn"
                disabled={loading || checking}
                title={t('voterSurvey.submitButton') || 'Submit Survey'}
              >
                {loading 
                  ? t('voterSurvey.submitting') || 'Submitting...' 
                  : t('voterSurvey.submitButton') || 'Submit Survey'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default VoterSurvey;
